import i18n from '@/plugins/i18n'
import Storage from '@/utils/storage'
import CookieStorage from '@/utils/cookie-storage'
import CheckoutAPI from '@/api/checkout'
import { parseQueryString } from '@/utils/query-string'
import { RIGHT_TO_LEFT_LANGS } from '@/utils/i18n'
import setAttributesByLocale from '@/utils/set-locale-attrs'
import { isEmbedded } from '@/utils/iframe-support'
import ApplicationVariantStorage from '@/utils/application-variant-storage'

const { search = '' } = window.location || {}
const { checkoutFrame } = parseQueryString(search)
const platform = Storage.getPlatform() || checkoutFrame

const _makeLocaleKey = (module, locale) => {
  return `${module}_${locale}`
}

if (platform) {
  Storage.setPlatform(platform)
}

const state = {
  locale: '',
  messages: {},
  screenWidth: 0,
  screenHeight: 0,
  direction: 'ltr',
  platform,
  fetching: false,
  deepIntegration: false,
  redirecting: false,
  fetchedMessages: {},
  variants: ApplicationVariantStorage.getApplicationVariants(),
  showCart: false,
  showFarahUnavailable: false,
  showCardVerification: false,
}

const getters = {
  setLocale: (state) => {
    return !!state.locale
  },
  showCart: (state) => {
    return state.showCart
  },
  showFarahUnavailable: (state) => {
    return state.showFarahUnavailable
  },
  showCardVerification: (state) => {
    return state.showCardVerification
  },
  messages: (state) => {
    return state.messages || {}
  },
  currentLocale: (state) => {
    return state.locale.replace('-', '_')
  },
  currentLang: (state) => {
    const locale = state.locale.replace('-', '_')
    const [lang] = locale.split('_')

    return lang || 'en'
  },
  getDirection: (state) => {
    return state.direction
  },
  isArabic: (_, getters) => {
    return getters.currentLang === 'ar'
  },
  hasLocale: (state) => {
    return !!state.locale
  },
  isEmbedded() {
    return isEmbedded()
  },
  isIframe() {
    return window.top !== window
  },
  currentDirection: (state) => {
    return state.direction
  },
  isRightToLeftDirection: () => {
    return state.direction === 'rtl'
  },
  screenHeight: (state) => {
    return state.screenHeight
  },
  screenWidth: (state) => {
    return state.screenWidth
  },
  isMobileScreen: (state) => {
    const screenWidth = state.screenWidth

    return screenWidth <= 600
  },
  isUqudoSmallMobileScreen: (state) => {
    // check if the screen is small mobile screen width and height
    const screenWidth = state.screenWidth

    return screenWidth <= 990
  },
  isUqudoMobileScreen: (state) => {
    // check if the screen is small mobile screen width and height
    const screenWidth = state.screenWidth

    return screenWidth <= 1024
  },
  applicationVariants: (state) => ({
    landingVariant: state.variants?.landingVariant || '',
    payTheDifferenceDisclaimer:
      state.variants?.payTheDifferenceDisclaimer || '',
    idMatchAnotherUser: state.variants?.idMatchAnotherUser || '',
    payInFullValue: state.variants?.payInFullValue || '',
    ivr: state.variants?.ivr || '',
    paymentPlanContent: state.variants?.paymentPlanContent || '',
    paymentFeePopup: state.variants?.paymentFeePopup || '',
  }),
  fetchedI18nMessage: (state) => state.fetchedMessages || {},
}

const mutations = {
  TOGGLE_SHOW_CART: (state) => {
    return (state.showCart = !state.showCart)
  },
  TOGGLE_SHOW_FARAH_UNAVAILABLE: (state) => {
    return (state.showFarahUnavailable = !state.showFarahUnavailable)
  },
  TOGGLE_SHOW_CARD_VERIFICATION: (state, value) => {
    state.showCardVerification = value
  },
  UPDATE_LOCALE: (state, locale) => {
    state.locale = locale
  },
  UPDATE_MESSAGES: (state, { messages, locale }) => {
    state.messages = { ...state.messages, [locale]: messages }
  },
  SET_DIRECTION: (state, direction) => {
    state.direction = direction
  },
  SET_DEEP_INTEGRATION: (state, value) => {
    state.deepIntegration = value
  },
  SET_REDIRECTING: (state, value) => {
    state.redirecting = value
  },
  SET_PLATFORM: (state, value) => {
    state.platform = value || ''
  },
  SET_FETCHED_MESSAGES: (state, { module, locale }) => {
    state.fetchedMessages = {
      ...state.fetchedMessages,
      [_makeLocaleKey(module, locale)]: true,
    }
  },
  SET_SCREEN_SIZE: (state, { width, height }) => {
    state.screenWidth = width
    state.screenHeight = height
  },
  SET_APPLICATION_VARIANTS: (state, variants) => {
    state.variants = variants
  },
}

const actions = {
  init({ dispatch }) {
    window.addEventListener('resize', () => {
      dispatch('calculateScreenSize')
    })

    dispatch('calculateScreenSize')
  },
  calculateScreenSize({ commit }) {
    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth
    const height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight

    commit('SET_SCREEN_SIZE', { width, height })
  },
  toggleShowCart({ commit }) {
    commit('TOGGLE_SHOW_CART')
  },
  toggleShowFarahUnavailable({ commit }) {
    commit('TOGGLE_SHOW_FARAH_UNAVAILABLE')
  },
  toggleShowCardVerification({ commit }, value) {
    commit('TOGGLE_SHOW_CARD_VERIFICATION', value)
  },
  async setI18nMessageByLocale({ commit, dispatch }, locale) {
    try {
      commit('UPDATE_LOCALE', locale)

      i18n.locale = locale
      i18n.setLocaleMessage(locale, {})

      setAttributesByLocale(locale)

      CookieStorage.setStickyLocale(locale)

      return true
    } catch (error) {
      console.error(error)

      return false
    }
  },
  async fetchMessages({ commit, dispatch, getters }, module) {
    const locale = getters.currentLocale || 'ar_SA'

    const messages = await CheckoutAPI.fetchMessages(module, locale)

    commit('UPDATE_MESSAGES', { messages, locale })
    commit('SET_FETCHED_MESSAGES', { module, locale })

    // Trigger change direction
    dispatch('setDirectionByLocale', locale)

    const messagesObj = messages || {}

    i18n.mergeLocaleMessage(locale, { ...messagesObj })

    dispatch('setDirectionByLocale', locale)

    return messages
  },
  isFetchedI18nMessage({ state, getters }, module) {
    const locale = getters.currentLocale || 'ar_SA'

    return state.fetchedMessages[_makeLocaleKey(module, locale)] ?? false
  },
  setDirectionByLocale({ commit }, locale) {
    const [lang] = locale.replace('-', '_').split('_')
    let direction = 'ltr'

    if (RIGHT_TO_LEFT_LANGS.includes(lang)) {
      direction = 'rtl'
    }

    commit('SET_DIRECTION', direction)

    return direction
  },
  updateApplicationVariants({ commit, getters }, variants) {
    if (!TypeChecker.isObject(variants)) {
      return
    }

    const currentVariants = getters.applicationVariants
    const nextVariants = {
      ...currentVariants,
      ...variants,
    }

    commit('SET_APPLICATION_VARIANTS', nextVariants)

    ApplicationVariantStorage.setApplicationVariants(nextVariants)
  },
  setDeepIntegration({ commit }, value) {
    commit('SET_DEEP_INTEGRATION', value)
  },
  setRedirecting({ commit }, value) {
    commit('SET_REDIRECTING', value)
  },
  clearSession({ dispatch, commit }) {
    Storage.clearPlatform()
    Storage.clearIdNumber()
    Storage.clearViewedPaymentLinkLandingPage()

    ApplicationVariantStorage.removeApplicationVariants()

    commit('SET_PLATFORM', checkoutFrame)

    dispatch('checkout/clearSession', null, { root: true })
    dispatch('payment/clearSession', null, { root: true })
    dispatch('session/resetSession', null, { root: true })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
